type ScrollLocation = { key: string; pathname: string; search: string; hash: string };
type SavedPosition = { x: number; y: number; sectionId: string; offset: number };

const restorationWindowMs = 1600;

function isRestoredNavigation(browser: Pick<Window, "performance">) {
  const navigation = browser.performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
  return navigation?.type === "reload" || navigation?.type === "back_forward";
}

/** Let the browser restore the reading position on reload or history traversal
   (dental solution page and the localized home pages). */
export function createDentalScrollRestorationGuard(browser: Pick<Window, "performance" | "location">) {
  let preserving = isRestoredNavigation(browser)
    && (/\/solutions\/dental-clinics\/?$/.test(browser.location.pathname)
      || /^\/(cn\/|hk\/)?ai-receptionist\/?$/.test(browser.location.pathname)
      || /^\/$/.test(browser.location.pathname));
  let initialEntry: string | undefined;

  return (location: ScrollLocation) => {
    if (!preserving) return false;
    const entry = JSON.stringify([location.key, location.pathname, location.search, location.hash]);
    initialEntry ??= entry;
    // Keep this stable through StrictMode effect replay, but release it permanently
    // after the first link/route change. A reload must not disable later navigation.
    if (entry !== initialEntry) preserving = false;
    return preserving;
  };
}

/** Retain a per-tab, per-history-entry position when native Chrome restoration falls back to zero. */
export function installDentalScrollRestoration(main: HTMLElement) {
  const document = main.ownerDocument;
  const browser = document.defaultView;
  if (!browser) return () => {};
  const pathname = browser.location.pathname;
  const storageKey = () => `clawease:dental-scroll:v1:${JSON.stringify([
    browser.location.pathname, browser.location.search, browser.history.state?.key ?? "default",
  ])}`;
  let saved: SavedPosition | null = null;
  if (isRestoredNavigation(browser)) {
    try {
      const value = JSON.parse(browser.sessionStorage.getItem(storageKey()) ?? "null");
      if (value && Number.isFinite(value.x) && value.x >= 0 && Number.isFinite(value.y) && value.y >= 0
        && typeof value.sectionId === "string" && Number.isFinite(value.offset)) saved = value;
    } catch { /* Storage may be disabled; native restoration remains available. */ }
  }
  let restoring = saved !== null;
  let restoreFrame = 0;
  let saveFrame = 0;
  const previousRestoration = browser.history.scrollRestoration;

  const save = () => {
    browser.cancelAnimationFrame(saveFrame);
    saveFrame = 0;
    if (restoring || !main.isConnected || browser.location.pathname !== pathname) return;
    const center = browser.innerHeight / 2;
    const section = Array.from(main.querySelectorAll<HTMLElement>(":scope > section[id]")).find((element) => {
      const rect = element.getBoundingClientRect();
      return rect.top <= center && rect.bottom > center;
    });
    const position: SavedPosition = {
      x: Math.max(0, browser.scrollX), y: Math.max(0, browser.scrollY),
      sectionId: section?.id ?? "", offset: section ? -section.getBoundingClientRect().top : 0,
    };
    try { browser.sessionStorage.setItem(storageKey(), JSON.stringify(position)); } catch { /* Optional storage. */ }
  };
  const scheduleSave = () => {
    if (!restoring && !saveFrame) saveFrame = browser.requestAnimationFrame(save);
  };
  const release = () => {
    if (!restoring) return;
    restoring = false;
    browser.cancelAnimationFrame(restoreFrame);
    browser.history.scrollRestoration = previousRestoration;
  };
  const onInput = () => { release(); scheduleSave(); };
  const onPageHide = () => { save(); release(); };

  if (saved) {
    // Restore before paint, then cover the brief hydration/font/layout window.
    // Never replace the saved value with the temporary zero position during loading.
    browser.history.scrollRestoration = "manual";
    const position = saved;
    const started = browser.performance.now();
    const restore = (now: number) => {
      restoreFrame = 0;
      if (!restoring) return;
      const section = position.sectionId ? document.getElementById(position.sectionId) : null;
      const desired = section && main.contains(section)
        ? section.getBoundingClientRect().top + browser.scrollY + position.offset
        : position.y;
      const maxScroll = Math.max(0, document.documentElement.scrollHeight - browser.innerHeight);
      const top = Math.max(0, Math.min(maxScroll, desired));
      if (Math.abs(browser.scrollY - top) > 0.5 || Math.abs(browser.scrollX - position.x) > 0.5) {
        browser.scrollTo({ top, left: position.x, behavior: "instant" });
      }
      if (now - started < restorationWindowMs) restoreFrame = browser.requestAnimationFrame(restore);
      else { release(); save(); }
    };
    restore(started);
  }

  const inputs = ["wheel", "touchstart", "pointerdown", "keydown"] as const;
  inputs.forEach((name) => browser.addEventListener(name, onInput, { passive: true }));
  browser.addEventListener("scroll", scheduleSave, { passive: true });
  browser.addEventListener("pagehide", onPageHide);
  // Clicking a link must also cancel any pending restoration before its target scrolls.
  browser.addEventListener("hashchange", onInput);
  if (!saved) scheduleSave();
  return () => {
    save();
    release();
    browser.cancelAnimationFrame(saveFrame);
    inputs.forEach((name) => browser.removeEventListener(name, onInput));
    browser.removeEventListener("scroll", scheduleSave);
    browser.removeEventListener("pagehide", onPageHide);
    browser.removeEventListener("hashchange", onInput);
  };
}
